import { Combobox } from '@/components/ui/combobox/combobox';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import useDeal from '@/hooks/use-deal';
import useLenders from '@/hooks/use-lenders';
import { HOTKEYS } from '@/lib/hotkeys';
import type { Deal } from '@/models/registry';
import type { Lender } from '@/models/types';
import { noop } from 'lodash';
import { useState } from 'react';
import useLenderMutations from './use-lender-mutations';
import useLenderOptions from './use-lender-options';

type DealLenderSelectorCombobox = {
  dealId: Deal['id'];
  shortId: Deal['short_id'];
  afterOnChange?: () => void;
  onUpdateSuccess?: (data: null, variables: { lender: Lender }) => void;
  onUpdateError?: (error: Error, variables: { lender: Lender }) => void;
  onCreateSuccess?: (data: null, variables: { lender: Lender }) => void;
  onCreateError?: (error: Error, variables: { lender: Lender }) => void;
};

function DealLenderSelectorCombobox({
  dealId,
  shortId,
  afterOnChange,
  onUpdateSuccess,
  onUpdateError,
  onCreateSuccess,
  onCreateError,
}: DealLenderSelectorCombobox) {
  const { lenders } = useLenders();
  const { lenderOptions } = useLenderOptions();
  const { deal } = useDeal(shortId);
  const lender = deal?.lender;

  // TECH DEBT: remove this while preserving related functionality
  const [, setDialogIsOpen] = useState(false);

  const { updateLenderMutation, deleteLenderMutation } = useLenderMutations({
    dealId,
    dealShortId: shortId,
    setDialogIsOpen,
    onUpdateSuccess,
    onUpdateError,
    onCreateSuccess,
    onCreateError,
  });

  const handleLenderChange = (value: string) => {
    if (value === 'delete' && lender) {
      deleteLenderMutation.mutate({
        lender,
      });
      return;
    }

    const innerLender = lenders.find((lender) => lender.id === value);

    if (!innerLender) {
      return;
    } else {
      updateLenderMutation.mutate({
        lender: innerLender,
      });
    }

    afterOnChange?.();
  };

  return (
    <Combobox
      endAdornment={<KeyboardKeyIcon>{HOTKEYS.DEAL_LENDER}</KeyboardKeyIcon>}
      placeholder="Set Lender..."
      emptyMessage="No Lender found"
      options={lenderOptions}
      onKeyDown={(e) => {
        const keyPressed = e.key;

        const option = lenderOptions.find(
          (option) => option.hotkey === keyPressed,
        );

        if (option) {
          handleLenderChange(option.value);
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onChange={handleLenderChange}
      setOpen={noop}
    ></Combobox>
  );
}

export default DealLenderSelectorCombobox;
